<template>
  <div class="panel" :class="opened ? 'open' : ''">

    <!-- 開閉タブ（閉じているときはこれだけが見える） -->
    <div class="tab" @click="opened = !opened">
      <span class="tab-arrow">{{ opened ? '▶' : '◀' }}</span>
      <span class="tab-text">【雇い入れ】サブタスク</span>
      <span v-if="openCount" class="tab-count">
        <span class="tab-count-label">未完了</span>
        {{ openCount }}
      </span>
    </div>

    <div class="body">
      <!-- 見出しは 2 段（1 段目：名前と完了数、2 段目：ボタン）。1 段に並べると窮屈で名前が折れる -->
      <div class="head">
        <div class="head-row">
          <span class="head-text">【雇い入れ】サブタスク</span>
          <span class="head-sub">{{ doneCount }} / {{ visibleTasks.length }} 完了</span>
        </div>
        <div class="head-row">
          <!-- サブタスク一覧へ移る（右上の「一覧」ドロップダウンの「サブタスク一覧」と同じ）。パネルを閉じてから一覧を押す手間を省く
               保存していない変更があれば親の beforeRouteLeave が confirmLeave で確かめる -->
          <a href="#" class="list-link" v-tooltip="'サブタスク一覧へ移ります'" @click.prevent="toSubList">サブ一覧へ</a>
          <button type="button" class="add-btn" @click="add">
            <Icon iconName="Plus" iconType="outline" :strokeWidth="2" class="h-4 w-4 mr-1" />
            追加
          </button>
          <!-- サブタスクはAKBSとは別に、ここの「更新」で保存する。保存していない変更があるときだけ目立たせる
               メインタスクが完了していれば保存させない（更新を出さない） -->
          <button v-if="!isMainDone" type="button" class="save-btn" :class="dirty ? 'dirty' : ''" v-tooltip="dirty ? '保存していない変更があります' : ''" @click="save">
            <Icon iconName="Save" iconType="outline" :strokeWidth="2" class="h-4 w-4 mr-1" />
            更新
          </button>
          <div v-else class="main-done">メイン完了済み</div>
        </div>
      </div>

      <div v-if="dirty" class="unsaved">保存していない変更があります。「更新」を押してください。</div>

      <div v-if="!visibleTasks.length" class="empty">サブタスクがありません。</div>

      <!-- 並べ替えは掴んで動かす（つまみは左端の点々） -->
      <draggable
        ref="list"
        :list="tasks"
        direction="vertical"
        handle=".drag-handle"
        class="list"
        @change="setDisplayOrder"
      >
        <!-- 削除したものは deleted を立てて隠すだけ（配列には残す）
             並べ替えは DOM の並びで位置を決めるため、v-if で外さず v-show で隠す -->
        <div
          v-for="(task, index) in tasks"
          v-show="!task.deleted"
          :key="'subtask' + index"
          class="task"
          :class="task.status === 'done' ? 'done' : ''"
        >
          <div class="drag-handle" v-tooltip="'掴んで並べ替えます'">
            <Icon iconName="SwitchVertical" iconType="outline" :strokeWidth="2" class="w-4 h-7" />
          </div>

          <div class="task-body">
            <!-- 1行目：サブタスク名・期限 -->
            <div class="task-row">
              <!-- サブタスク名はサブタスクマスタ（カテゴリー「雇い入れ（清掃員）」の分）から選ぶ。候補に無い名前もそのまま打てる
                   候補を選ぶと、マスタの部署・担当者・期限が入る（タスク依頼の画面と同じ） -->
              <WmsSuggestInput
                :name="'requestTask' + index"
                caption="サブタスク名"
                displayField="name"
                :dynamic="false"
                :incrementalSearch="false"
                :selectionItems="taskMasterList"
                :filter="{}"
                :clearable="true"
                :required="true"
                v-model="task.requestTask"
                class="flex-1"
                @selected="selectedTaskName(task, $event)"
              />
              <!-- 期限はメインタスクの期限まで -->
              <WmsDateField
                :name="'limitDate' + index"
                caption="期限"
                v-model="task.limitDate"
                :max="rp.limitDate"
                :clearable="true"
                :required="true"
                class="w-32"
              />
            </div>

            <!-- 2行目：部署・担当者 -->
            <div class="task-row">
              <WmsSuggestInput
                :name="'departmentName' + index"
                caption="部署"
                displayField="name"
                :dynamic="false"
                :incrementalSearch="false"
                :selectionItems="departmentList"
                :filter="{}"
                :clearable="true"
                :required="true"
                v-model="task.departmentName"
                class="flex-1"
                @selected="selectedDepartment(task, $event)"
              />
              <!-- 担当者の候補は部署を選んでいればその部署の人だけ、選んでいなければ全員 -->
              <WmsSuggestInput
                :name="'toStaffName' + index"
                caption="担当者"
                displayField="userName"
                :dynamic="false"
                :incrementalSearch="false"
                :selectionItems="staffList(task)"
                :filter="{}"
                :clearable="true"
                :required="true"
                v-model="task.toStaffName"
                class="flex-1"
                @selected="selectedStaff(task, $event)"
              />
            </div>

            <!-- 3行目：最終・完了・削除 -->
            <div class="task-row">
              <!-- 最終完了タスク（ラジオボタンで1つだけ。他のサブタスクが全部完了しないと完了にできない） -->
              <label
                class="final-radio"
                :class="isFinal(task) ? 'checked' : ''"
                v-tooltip="'最終タスクは他のサブタスクが全部完了しないと完了にできません（1つだけ選べます）'"
              >
                <input
                  type="radio"
                  name="finalCompletionTask"
                  value="1"
                  :checked="isFinal(task)"
                  @change="setFinal(task)"
                />
                最終タスク
              </label>

              <!-- 押すたびに完了・未完了を切り替える -->
              <button
                type="button"
                class="done-btn"
                :class="task.status === 'done' ? 'checked' : ''"
                v-tooltip="task.status === 'done' ? 'クリックすると未完了に戻します' : 'クリックすると完了にします'"
                @click="toggleDone(task)"
              >
                <Icon iconName="Check" iconType="outline" :strokeWidth="3" class="w-4 h-4 mr-1" />
                {{ task.status === 'done' ? '完了' : '未完了' }}
              </button>

              <!-- 「●●送付」のサブタスク：出力画面を別タブで開く（清掃員にチェック・一式 ON の状態。バックの akbsOutput/taskLink が決める。名前に「備品」も含めば入社用具一式） -->
              <button v-if="isSendTask(task) && !isBugyoTask(task)" type="button" class="icon-btn output" v-tooltip="'出力画面を別タブで開きます（この清掃員にチェックが入った状態）'" @click="openOutput(task)">
                出力画面を開く
              </button>

              <!-- 「給与奉行●●」のサブタスク：出力画面の「給与奉行」タブを別タブで開く（人の検索はしない。「未出力の清掃員のみ」が ON） -->
              <button v-if="isBugyoTask(task)" type="button" class="icon-btn output" v-tooltip="'出力画面の「給与奉行」タブを別タブで開きます'" @click="openOutputBugyo()">
                出力画面を開く
              </button>

              <!-- 「●●返送確認」のサブタスク：出力画面の「契約書返送」タブを、この清掃員コードで検索した状態で別タブに開く -->
              <button v-if="isContractReturnTask(task)" type="button" class="icon-btn output" v-tooltip="'出力画面の「契約書返送」タブを、この清掃員で検索した状態で別タブに開きます'" @click="openContractReturn()">
                契約書返送を開く
              </button>

              <!-- 「●●返送保管」のサブタスク：登録済み一覧でこの清掃員の「書類」タブを別タブに開く -->
              <button v-if="isDocKeepTask(task)" type="button" class="icon-btn output" v-tooltip="'登録済み一覧でこの清掃員の「書類」タブを別タブに開きます'" @click="openRegisteredDocs()">
                書類を開く
              </button>

              <!-- 「交通費・緊急連絡先登録」のサブタスク：登録済み一覧でこの清掃員の「緊急連絡先」タブを別タブに開く -->
              <button v-if="isEmergencyTask(task)" type="button" class="icon-btn output" v-tooltip="'登録済み一覧でこの清掃員の「緊急連絡先」タブを別タブに開きます'" @click="openRegisteredEmergency()">
                緊急連絡先を開く
              </button>

              <!-- 「求人確認」のサブタスク：AKBS の「欠員募集」画面を別タブで開く -->
              <button v-if="isRecruitTask(task)" type="button" class="icon-btn output" v-tooltip="'AKBS の「欠員募集」画面を別タブで開きます'" @click="openAkbsRecruit()">
                欠員募集へ
              </button>

              <button type="button" class="icon-btn warn ml-auto" v-tooltip="'このサブタスクを取り除きます'" @click="remove(index)">
                <Icon iconName="Trash" iconType="outline" :strokeWidth="2" class="w-4 h-4 mr-1" />
                削除
              </button>
            </div>
          </div>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import * as taskRequestManager from '@managers/taskRequestManager'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import * as moment from 'moment'
import draggable from 'vuedraggable'
import { AKBS_STATUS, AKBS_SUB_TASK, isAkbsSubTask } from '@libs/constantsAKBS'
import { isSendTask, openAkbsOutputFromTask, isRecruitTask, openAkbsRecruit, isBugyoTask, openAkbsOutputBugyo, isContractReturnTask, openAkbsContractReturn, isDocKeepTask, openAkbsRegisteredDocs, isEmergencyTask, openAkbsRegisteredEmergency } from '@libs/akbsOutputLink'

// 必須項目（「更新」で保存するときに空だと止める）
const REQUIRED_ITEMS = [
  { key: 'requestTask', caption: 'サブタスク名' },
  { key: 'limitDate', caption: '期限' },
  { key: 'departmentName', caption: '部署' },
  { key: 'toStaffName', caption: '担当者' },
]

/**
 * 雇い入れのサブタスク（AKBSとは関係ないただのTODO）
 * 取得は親がまとめて行い value で受け取るが、編集・保存・未保存の判定はこの中で完結させる
 */
export default {
  components: {
    Icon,
    WmsDateField,
    WmsSuggestInput,
    draggable
  },

  props: {
    // 取得したサブタスク（親が取得したもの。ここで複製して編集する）
    value: {
      type: Array,
      default: () => []
    },

    // メインタスク（サブタスクを新しく作るときに紐づける）
    rp: {
      type: Object,
      default: () => ({})
    },

    // 未完了が残っていたら自動で開くか（承認者が開いたときは false にして、変更箇所の確認を邪魔しない）
    autoOpen: {
      type: Boolean,
      default: true
    },

    // 清掃員の申請の今の状況（req_akbs.status）
    // サブタスク「雇用指示登録申請」「雇用指示承認」は手で完了にできず、この状況で決まる（決まりはバックの akbsSubTask.js。DB はバックが直す）
    appStatus: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // 開閉
      opened: false,

      // 編集中のサブタスク（value の複製）
      tasks: [],

      // 最後に取得・保存したときの内容（JSON文字列。保存していない変更があるかの判定用）
      saved: '[]',

      // サブタスク名の候補（サブタスクマスタのうち、カテゴリー「雇い入れ（清掃員）」のもの）
      taskMasterList: []
    }
  },

  async created() {
    // 候補はカテゴリー「雇い入れ（清掃員）」の分だけ（面接の申請から開いても、ここで足すサブタスクは雇い入れのもの）
    this.taskMasterList = await taskRequestManager.getTaskMstByCategory(this, ['雇い入れ（清掃員）'])
  },

  computed: {
    /**
     * メインタスクが完了しているか（完了していればサブタスクは保存させない）
     */
    isMainDone() {
      return this.rp.status === 'done'
    },

    /**
     * 保存していない変更があるか（最後に取得・保存したときの内容と比べる）
     * メインタスクが完了していれば保存できないので、変更があっても知らせない
     */
    dirty() {
      return !this.isMainDone && JSON.stringify(this.tasks) !== this.saved
    },

    /**
     * 画面に出すサブタスク（削除したものは除く）
     */
    visibleTasks() {
      return this.tasks.filter(task => !task.deleted)
    },

    /**
     * 部署の候補
     */
    departmentList() {
      return this.$store.getters.departmentList || []
    },

    /**
     * 完了した数
     */
    doneCount() {
      return this.visibleTasks.filter(task => task.status === 'done').length
    },

    /**
     * 残っている数（閉じていても分かるようタブに出す）
     */
    openCount() {
      return this.visibleTasks.length - this.doneCount
    }
  },

  watch: {
    /**
     * 親が取得し直したら編集中の内容も入れ替える
     * ただし保存していない変更があるときは、それを消さないよう入れ替えない
     * （親はAKBSの保存後にも取り直すため）
     */
    value: {
      handler(value) {
        if (!this.dirty) {
          this.setTasks(value)
        }
      },
      immediate: true
    },

    /**
     * 申請の状況が変わった（申請・申請取消・差戻・AKBSへ登録）：雇用指示登録申請・雇用指示承認の完了を合わせ直す
     * DB はバックがもう直しているので、「保存済み」の控えにも同じ内容を入れる（これだけで「保存していない変更あり」にしないため）
     */
    appStatus() {
      this.applyAkbsAuto(this.tasks)
      const saved = JSON.parse(this.saved)
      this.applyAkbsAuto(saved)
      this.saved = JSON.stringify(saved)
    },

    /**
     * 未完了が残っていれば開いた状態で見せる（やることがあると気づけるように）
     * 一度開いたあとは、全部完了しても勝手には閉じない
     * autoOpen が false（承認者）のときは開かない（タブの未完了の数で気づける）
     */
    openCount: {
      handler(count) {
        if (count && this.autoOpen) {
          this.opened = true
        }
      },
      immediate: true
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.warnUnsaved)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnUnsaved)
  },

  methods: {
    /**
     * ドロワーを閉じる（親の「全て閉じる」から呼ぶ。編集中の内容はそのまま残る）
     */
    close() {
      this.opened = false
    },

    /**
     * 編集中の内容を入れ替え、それを「保存済み」として控える
     * @param {Array} tasks サブタスク
     */
    setTasks(tasks) {
      // deleted・finalCompletionTask は後から足すとリアクティブにならないため、読み込み時にキーを揃えておく
      this.tasks = utils.clone(tasks || []).map(task => ({ deleted: false, finalCompletionTask: '', ...task }))
      this.applyAkbsAuto(this.tasks)
      this.saved = JSON.stringify(this.tasks)
    },

    /**
     * 完了を手で切り替えられないサブタスクか（雇用指示登録申請・雇用指示承認）
     * 申請したら「雇用指示登録申請」が、AKBS に登録したら「雇用指示承認」が自動で完了になる（申請取消・差戻で登録申請は未完了に戻る）
     */
    isAkbsAuto(task) {
      return isAkbsSubTask(task.requestTask, AKBS_SUB_TASK.EMPLOYMENT) || isAkbsSubTask(task.requestTask, AKBS_SUB_TASK.EMPLOYMENT_APPROVE)
    },

    /**
     * 申請の状況に合わせて、サブタスク「雇用指示登録申請」「雇用指示承認」の完了／未完了を入れる
     * 承認待ち：登録申請だけ完了、AKBS登録済み：両方完了、それ以外：両方未完了
     * DB の方はバックが同じ決まりで直しているので、ここは画面の表示と、「更新」で古い状態を書き戻さないためのもの
     * @param {Array} list サブタスクの配列
     */
    applyAkbsAuto(list) {
      const entryDone = [AKBS_STATUS.PENDING, AKBS_STATUS.REGISTERED].includes(this.appStatus)
      const approveDone = this.appStatus === AKBS_STATUS.REGISTERED
      for (const task of list) {
        // まだ保存していないサブタスク（_id が無い）には完了が無いので触らない（「更新」のあとにバックが合わせる）
        if (!task._id || !this.isAkbsAuto(task)) continue
        const done = isAkbsSubTask(task.requestTask, AKBS_SUB_TASK.EMPLOYMENT_APPROVE) ? approveDone : entryDone
        if (done && task.status !== 'done') {
          task.status = 'done'
          task.doneDate = moment().format('YYYY-MM-DD HH:mm:ss')
          task.doneStaffName = this.$store.getters.user.user_name
        } else if (!done && task.status === 'done') {
          task.status = 'open'
          task.doneDate = null
          task.doneStaffName = null
        }
      }
    },

    /**
     * 保存（「更新」ボタン）
     * 作る・直す・消すの判定はバック側で行うため、配列ごと渡す
     */
    async save() {
      // 必須項目（サブタスク名・期限・部署・担当者）が空のサブタスクがあれば保存しない
      const errors = this.requiredErrors()
      if (errors.length) {
        await dialogs.showErrorDialog('更新できません', '未入力の必須項目があります。\n\n・' + errors.join('\n・'))
        return
      }

      // 期限がメインタスクの期限より後のサブタスクがあれば保存しない（日付入力の max だけでは手入力を防げないため）
      const over = this.overLimitErrors()
      if (over.length) {
        await dialogs.showErrorDialog('更新できません', `サブタスクの期限はメインタスクの期限（${this.rp.limitDate}）までにしてください。\n\n・` + over.join('\n・'))
        return
      }

      // 自動で完了になったサブタスク（雇用指示登録申請・雇用指示承認）が最終完了タスクで、未完了がまだ残っているなら、
      // 最終完了タスクを残っている中で一番下へ付け替える（下の決まりで止まるが、手では未完了に戻せないため。タスク依頼の画面と同じ）
      const autoFinal = this.visibleTasks.find(task => this.isFinal(task) && task.status === 'done' && this.isAkbsAuto(task))
      if (autoFinal) {
        const open = this.visibleTasks.filter(task => task !== autoFinal && task.status !== 'done')
        if (open.length) {
          this.setFinal(open[open.length - 1])
        }
      }

      // サブタスクがあるなら、最終完了タスクを1つ決めていないと保存しない（タスク依頼の画面と同じ決まり）
      const final = this.visibleTasks.find(task => this.isFinal(task))
      if (this.visibleTasks.length && !final) {
        await dialogs.showInfoDialog('最終完了タスクを選んでください', 'サブタスクのどれか1つを「最終」にしてから更新してください。', 'OK')
        return
      }

      // 最終完了タスクが完了しているのに他に未完了があれば保存しない（タスク依頼の画面と同じ決まり）
      if (final && final.status === 'done' && this.hasOpenOther(final)) {
        await dialogs.showInfoDialog('最終完了タスクは完了できません', '未完了のサブタスクがあるため、最終完了タスクも未完了に戻してください。', 'OK')
        return
      }

      // 保存中は親に知らせて画面全体を覆ってもらう
      this.$emit('loading', true)
      try {
        // 未完了のものは完了日・完了した人を空にして渡す（完了→未完了に戻したものは画面上に残しているため、ここで消す）
        const subTasks = this.tasks.map(task => (task.status === 'done' ? task : { ...task, doneDate: null, doneStaffName: null }))

        const res = await backend.postData('akbsInputForm/saveSubTasks', { subTasks })

        // 保存後のもの（採番済み。削除したものは含まない）で全部入れ替え、それを「保存済み」として控える
        this.setTasks(res.data.data.subTasks)

        await dialogs.showSuccessDialog('サブタスクを更新しました', '')
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('更新エラー', 'サブタスクの更新に失敗しました。もう一度お試しください。')
      } finally {
        this.$emit('loading', false)
      }
    },

    /**
     * 必須項目が空のサブタスクを「何件目の何」の形で列挙する（削除したものは除く）
     * @returns {Array} 空のもの（無ければ空配列）
     */
    requiredErrors() {
      const errors = []
      this.visibleTasks.forEach((task, i) => {
        const name = task.requestTask ? `「${task.requestTask}」` : `${i + 1}件目`
        for (const item of REQUIRED_ITEMS) {
          if (!task[item.key]) {
            errors.push(`${name} の ${item.caption}`)
          }
        }
      })
      return errors
    },

    /**
     * 期限がメインタスクの期限より後のサブタスクを列挙する（削除したものは除く）
     * @returns {Array} 該当するもの（無ければ空配列）
     */
    overLimitErrors() {
      const limit = this.rp.limitDate
      if (!limit) {
        return []
      }

      return this.visibleTasks
        .filter(task => task.limitDate && task.limitDate > limit)
        .map((task, i) => `${task.requestTask ? `「${task.requestTask}」` : `${i + 1}件目`}：${task.limitDate}`)
    },

    /**
     * サブタスク一覧へ移る（右上の「一覧」ドロップダウンの「サブタスク一覧」と同じ画面。同じタブ）
     */
    toSubList() {
      this.$router.push({ name: 'RequestPostProgressList' })
    },

    /**
     * 別の画面へ移ってよいか（保存していない変更があれば確認する）
     * 親の beforeRouteLeave から呼ぶ
     * @returns {Boolean} 移ってよければ true
     */
    async confirmLeave() {
      if (!this.dirty) {
        return true
      }

      const dialog = await dialogs.showWarningConfirmDialog('サブタスクが保存されていません', 'サブタスクに保存していない変更があります。\nこのまま移動すると変更は失われます。\n\n移動しますか？', '移動する')
      return dialog === 'YES'
    },

    /**
     * ブラウザを閉じる・再読み込みの前に、保存していない変更があれば警告する
     * @param {Event} e beforeunload イベント
     */
    warnUnsaved(e) {
      if (this.dirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    },

    /**
     * 完了・未完了を切り替える
     * 保存は見出しの「更新」で行うため、ここでは状態を持つだけ
     * @param {Object} task 対象のサブタスク
     */
    async toggleDone(task) {
      // 雇用指示登録申請・雇用指示承認は手で切り替えられない（申請・AKBSへの登録で自動で完了になる）
      if (this.isAkbsAuto(task)) {
        await dialogs.showInfoDialog('手では切り替えられません', '「雇用指示登録申請」は申請すると、「雇用指示承認」はAKBSへ登録すると、自動で完了になります。', 'OK')
        return
      }

      // 最終完了タスクは、他のサブタスクが全部完了していないと完了にできない
      if (task.status !== 'done' && this.isFinal(task) && this.hasOpenOther(task)) {
        await dialogs.showErrorDialog('最終完了タスクは完了できません', '未完了のサブタスクがあります。\n他のサブタスクを全部完了にしてから、最終完了タスクを完了にしてください。')
        return
      }

      task.status = task.status === 'done' ? 'open' : 'done'

      // 完了にしたとき、完了した人がまだ無ければログインユーザーと今の日時を入れる
      // （未完了に戻しても消さないので、一度完了した人と日時はそのまま残る）
      if (task.status === 'done' && !task.doneStaffName) {
        task.doneDate = moment().format('YYYY-MM-DD HH:mm:ss')
        task.doneStaffName = this.$store.getters.user.user_name
      }
    },

    /**
     * 最終完了タスクか（タスク依頼の画面と同じく '1' で持つ）
     * @param {Object} task 対象のサブタスク
     */
    isFinal(task) {
      return task.finalCompletionTask == '1'
    },

    /**
     * 対象以外に未完了のサブタスクがあるか（削除したものは除く）
     * @param {Object} task 対象のサブタスク
     */
    hasOpenOther(task) {
      return this.visibleTasks.some(t => t !== task && t.status !== 'done')
    },

    /**
     * 最終完了タスクにする（1つだけなので、他は外す）
     * @param {Object} task 対象のサブタスク
     */
    setFinal(task) {
      for (const t of this.tasks) {
        t.finalCompletionTask = ''
      }
      task.finalCompletionTask = '1'
    },

    /**
     * 担当者の候補（タスク依頼の画面と同じく所属部署データから出す）
     * 部署を選んでいればその部署の人だけ、選んでいなければ全員（兼任で複数行ある人は1人にまとめる）
     * @param {Object} task 対象のサブタスク
     */
    staffList(task) {
      const ud = this.$store.getters.userDepartment || []

      if (task.departmentId) {
        return ud.filter(u => u.departmentId == task.departmentId)
      }

      const seen = {}
      return ud.filter(u => {
        if (seen[u.userId]) {
          return false
        }
        seen[u.userId] = true
        return true
      })
    },

    /**
     * 部署を選んだ
     * 担当者がその部署にいなければ担当者を空にする（タスク依頼の画面と同じ）
     * @param {Object} task 対象のサブタスク
     * @param {Object} e 選んだ部署
     */
    selectedDepartment(task, e) {
      task.departmentId = e ? e.departmentId : ''
      task.departmentName = e ? e.name : ''

      if (task.toStaffId && !this.staffList(task).some(u => u.userId == task.toStaffId)) {
        task.toStaffId = ''
        task.toStaffName = ''
      }
    },

    /**
     * 担当者を選んだ
     * @param {Object} task 対象のサブタスク
     * @param {Object} e 選んだ担当者
     */
    selectedStaff(task, e) {
      task.toStaffId = e ? e.userId : ''
      task.toStaffName = e ? e.userName : ''
    },

    /**
     * サブタスク名を候補から選んだ
     * マスタに設定されている部署・担当者と期限を入れる（タスク依頼の画面の setInitTask と同じ。taskRequestManager の setSubCharge・setSubLimitDate）
     * @param {Object} task 対象のサブタスク
     * @param {Object} e 選んだサブタスクマスタ
     */
    async selectedTaskName(task, e) {
      if (!e || !e.name) {
        return
      }
      task.requestTask = e.name

      const rp = this.rp || {}
      const store = this.$store.getters
      await taskRequestManager.setSubCharge(e, task, rp.toDepartment, rp.toDepartmentId, rp.toStaffId, rp.toStaffName, rp.byUserId, rp.byUser, store.departmentList, store.userDepartment, store.loginUserData)
      taskRequestManager.setSubLimitDate(task, e, rp)
    },

    /**
     * 出力画面を開くボタンを出すサブタスクか（名前に「送付」を含む）
     */
    isSendTask(task) {
      return isSendTask(task)
    },

    /**
     * 出力画面を別タブで開く（この雇い入れの清掃員にチェックが入り、申請の種別の一式が ON の状態）
     * API: akbsOutput/taskLink
     */
    async openOutput(task) {
      await openAkbsOutputFromTask(this, this.rp._id || this.rp.id, task)
    },

    /**
     * 出力画面の「給与奉行」タブを開くボタンを出すサブタスクか（名前に「給与奉行」を含む）
     */
    isBugyoTask(task) {
      return isBugyoTask(task)
    },

    /**
     * 出力画面の「給与奉行」タブを別タブで開く（人の検索はしない。「未出力の清掃員のみ」が ON の初期状態）
     */
    openOutputBugyo() {
      openAkbsOutputBugyo(this)
    },

    /**
     * 出力画面の「契約書返送」タブを開くボタンを出すサブタスクか（名前に「返送」と「確認」を含む）
     */
    isContractReturnTask(task) {
      return isContractReturnTask(task)
    },

    /**
     * 出力画面の「契約書返送」タブを、この雇い入れの清掃員コードで検索した状態で別タブに開く
     */
    async openContractReturn() {
      await openAkbsContractReturn(this, this.rp.employmentStaffNumber)
    },

    /**
     * 登録済み一覧の「書類」タブを開くボタンを出すサブタスクか（名前に「返送」と「保管」を含む）
     */
    isDocKeepTask(task) {
      return isDocKeepTask(task)
    },

    /**
     * 登録済み一覧でこの雇い入れの清掃員の「書類」タブを別タブに開く
     */
    async openRegisteredDocs() {
      await openAkbsRegisteredDocs(this, this.rp.employmentStaffNumber)
    },

    /**
     * 登録済み一覧の「緊急連絡先」タブを開くボタンを出すサブタスクか（名前に「緊急連絡先」を含む）
     */
    isEmergencyTask(task) {
      return isEmergencyTask(task)
    },

    /**
     * 登録済み一覧でこの雇い入れの清掃員の「緊急連絡先」タブを別タブに開く
     */
    async openRegisteredEmergency() {
      await openAkbsRegisteredEmergency(this, this.rp.employmentStaffNumber)
    },

    /**
     * AKBS の「欠員募集」画面を開くボタンを出すサブタスクか（名前に「求人確認」を含む）
     */
    isRecruitTask(task) {
      return isRecruitTask(task)
    },

    /**
     * AKBS の「欠員募集」画面を別タブで開く
     */
    openAkbsRecruit() {
      openAkbsRecruit()
    },

    /**
     * サブタスク追加
     */
    add() {
      const task = taskRequestManager.create()
      task.requestPostId = this.rp._id || this.rp.id
      task.taskRequestRequestNo = this.rp.requestNo
      task.status = 'open'
      task.deleted = false
      task.finalCompletionTask = ''

      this.tasks.push(task)

      // 追加ボタンは見出しにあり、行は一番下に増えるので、そこまで動かして名前の欄にフォーカスする
      this.$nextTick(() => {
        const list = this.$refs.list && this.$refs.list.$el
        if (!list) {
          return
        }
        list.scrollTop = list.scrollHeight
        const input = list.querySelector(`input[name="requestTask${this.tasks.length - 1}"]`)
        if (input) {
          input.focus()
        }
      })
    },

    /**
     * サブタスク削除
     * 保存済みのものは deleted を立てて隠すだけにし、「更新」でバックへ渡す（実際に消すかはバック側で行う）
     * まだ保存していないもの（採番が無い）はバックに無いので、配列から取り除く
     * @param {Number} index 対象の位置
     */
    remove(index) {
      const task = this.tasks[index]

      if (task.subTaskNo) {
        task.deleted = true
      } else {
        this.tasks.splice(index, 1)
      }

      this.setDisplayOrder()
    },

    /**
     * 並び順を今の並びに合わせる（削除したものは飛ばして数える）
     */
    setDisplayOrder() {
      this.visibleTasks.forEach((task, i) => {
        task.displayOrder = i
      })
    }
  }
}
</script>

<style scoped>
/* 入力画面の幅を変えないよう、右端に重ねて開く */
.panel {
  position: fixed;
  top: 5rem;
  right: 0;
  bottom: 1rem;
  z-index: 20;
  display: flex;
  /* 閉じているときは中身を右へ逃がし、タブだけ見せる（body の幅と揃える） */
  transform: translateX(460px);
  transition: transform 0.2s, top 0.2s;
}

/* 開いているときは画面の一番上から出し、ヘッダー右側の申請・一時保存・AKBSへ登録を本体で覆う
   （サブタスクの「更新」と押し間違えないように、開いている間はそれらを押せなくする） */
.panel.open {
  top: 0;
  z-index: 30;
  transform: translateX(0);
}

/* 開閉タブ。
   AKBSのデータはインディゴ系で揃えているため、サブタスクは別のものと分かるよう緑にする。
   長く見る画面なので、彩度は落として目が疲れないようにしている */
.tab {
  align-self: flex-start;
  margin-top: 12px;
  padding: 10px 6px;
  border: 1px solid #7cc4ad;
  border-right: none;
  border-radius: 6px 0 0 6px;
  background: #9bd5c2;
  /* 薄い緑の地なので、文字は濃い緑にしてはっきり読めるようにする */
  color: #1f4d3d;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: -2px 2px 4px rgba(47, 122, 99, 0.2);
}

.tab:hover {
  background: #8acdb6;
}

.tab-arrow {
  display: block;
  margin-bottom: 6px;
  text-align: center;
  font-size: 10px;
}

/* 縦書きにして細いタブに収める */
.tab-text {
  display: block;
  writing-mode: vertical-rl;
  letter-spacing: 0.15em;
}

/* 件数だけだと何の数か分からないので、同じ枠の中に「未完了」と添える（縦書き） */
.tab-count-label {
  display: block;
  margin: 0 auto 4px;
  font-size: 10px;
  writing-mode: vertical-rl;
  letter-spacing: 0.1em;
}

.tab-count {
  display: block;
  margin-top: 6px;
  padding: 6px 0 4px;
  border-radius: 3px;
  background: #2f7a63;
  color: #fff;
  text-align: center;
}

.body {
  display: flex;
  flex-direction: column;
  width: 460px;
  border: 1px solid #7cc4ad;
  border-right: none;
  border-radius: 6px 0 0 6px;
  background: #fff;
  box-shadow: -4px 0 10px rgba(47, 122, 99, 0.2);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid #7cc4ad;
  background: #9bd5c2;
  border-radius: 6px 0 0 0;
}

/* 1 段目は名前と完了数、2 段目は左端に「サブ一覧へ」の文字リンク、右にボタン */
.head-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.head-text {
  color: #1f4d3d;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.head-sub {
  margin-left: auto;
  color: #1f4d3d;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

/* 件数が増えてもここだけスクロールする。
   地を見出しと同じ色にして、1件ずつの白いカードが浮いて見えるようにする */
.list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px;
  background: #9bd5c2;
}

.empty {
  padding: 20px 0;
  text-align: center;
  color: #6b7280;
  font-size: 12px;
}

/* 1件は「つまみ｜中身｜削除」の3つ。中身は2行 */
.task {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  padding: 8px;
  border: 1px solid #7cc4ad;
  border-radius: 6px;
  background: #fff;
}

/* 完了したものは薄くして、残りに目が行くようにする */
.task.done {
  background: #f9fafb;
  opacity: 0.65;
}

.task-body {
  flex: 1;
  min-width: 0;
}

.task-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.task-row + .task-row {
  margin-top: 6px;
}

/* パネルは幅が限られるので、見出しも入力欄も1段小さくする。
   1件に項目が多くごちゃつくので、見出しは薄く細くして入力値の方が目立つようにする。
   子コンポーネントのラベルは Tailwind のクラス（設定で !important）が付いているため、
   こちらも !important を付けないと効かない */
.task-row >>> label:not(.final-radio) {
  font-size: 9px !important;
  font-weight: 400 !important;
  line-height: 1.2 !important;
  color: #9ca3af !important;
}

.task-row >>> input:not([type='radio']) {
  height: 30px !important;
  padding: 0 6px !important;
  font-size: 12px !important;
}

/* 掴んで並べ替えるつまみ。掴めることが分かるよう、枠を付けてボタンのように見せる */
.drag-handle {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  padding: 6px 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  cursor: grab;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.18);
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.drag-handle:hover {
  border-color: #4338ca;
  background: #eef2ff;
  color: #4338ca;
  box-shadow: 0 2px 5px rgba(49, 46, 129, 0.25);
}

/* 掴んでいる間は押し込んだように見せる */
.drag-handle:active {
  box-shadow: none;
  transform: translateY(1px);
  cursor: grabbing;
}

/* 完了・未完了を切り替えるボタン。押している状態が色で分かるようにする */
.done-btn {
  display: flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.done-btn:hover {
  border-color: #2f7a63;
  color: #2f7a63;
}

.done-btn.checked {
  border-color: #2f7a63;
  background: #2f7a63;
  color: #fff;
}

/* 最終完了タスクのラジオボタン。完了ボタンと押し間違えないよう、ボタンの見た目にはしない
   選ばれているときは青の太字にする */
.final-radio {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 4px;
  color: #6b7280;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
}

.final-radio input {
  width: 14px;
  height: 14px;
  margin: 0;
  cursor: pointer;
  accent-color: #1d4ed8;
}

.final-radio.checked {
  color: #1d4ed8;
  font-weight: 700;
}

.icon-btn {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.icon-btn:hover {
  border-color: #4338ca;
  color: #4338ca;
}

.icon-btn.warn:hover {
  border-color: #dc2626;
  color: #dc2626;
}

/* 「出力画面を開く」は別の画面へ行くボタンなので、未完了・削除と見分けがつくよう藍色（ユーザー指示） */
.icon-btn.output {
  border-color: #4338ca;
  background: #4338ca;
  color: #fff;
}

.icon-btn.output:hover {
  border-color: #3730a3;
  background: #3730a3;
  color: #fff;
}

/* サブ一覧への文字リンク（2 段目の左端）。見出しの緑の上で読めるよう濃い色の下線付き */
.list-link {
  color: #1e3a8a;
  font-size: 16px;
  font-weight: 900;
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
  cursor: pointer;
}

.list-link:hover {
  color: #1d4ed8;
}

/* 追加ボタンは見出しの右の方に置く（左の文字リンクとの間を空ける）。緑の見出しの上で目立つよう、白地にする */
.add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #2f7a63;
  border-radius: 4px;
  background: #fff;
  color: #2f7a63;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(47, 122, 99, 0.2);
  transition: background-color 0.15s, box-shadow 0.15s;
}

.add-btn:hover {
  background: #eef8f4;
  box-shadow: 0 2px 5px rgba(47, 122, 99, 0.25);
}

.add-btn:active {
  box-shadow: none;
  transform: translateY(1px);
}

/* 更新ボタン。teal-800 にして、保存する所だと分かるようにする（乗せると teal-900）
   保存していない変更があるときは白い枠で囲んで目立たせる
   （半透明の色は下の緑と混ざって濁るため、影も枠も不透明な色だけ使う） */
.save-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  /* 見出しの中で一番大きいボタンにして、ここで保存することが目に入るようにする */
  height: 40px;
  min-width: 110px;
  padding: 0 16px;
  border: 1px solid #134e4a;
  border-radius: 4px;
  background: #115e59;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s;
}

.save-btn:hover {
  background: #134e4a;
}

.save-btn.dirty {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

.save-btn:active {
  box-shadow: none;
  transform: translateY(1px);
}

/* メインタスクが完了していて保存できないとき、更新ボタンの代わりに出す */
.main-done {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  opacity: 0.85;
}

/* 保存していない変更があることの知らせ */
.unsaved {
  padding: 4px 12px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  font-weight: 700;
}
</style>
